// EnumOptions.js

import { normalizeTargetDurationDays } from 'utilities/PipelineDuration';

export const ENUM_OPTION_SORT_MODE_MANUAL = 'manual';
export const ENUM_OPTION_SORT_MODE_LABEL_ASC = 'label_asc';
export const ENUM_OPTION_SORT_MODE_LABEL_DESC = 'label_desc';
export const ENUM_OPTION_SORT_MODE_INHERIT = 'inherit';

export const ENUM_OPTIONS_MODE_FIXED = 'fixed';
export const ENUM_OPTIONS_MODE_MANAGED = 'managed';

export const ENUM_OPTION_SORT_MODES = Object.freeze([
  ENUM_OPTION_SORT_MODE_MANUAL,
  ENUM_OPTION_SORT_MODE_LABEL_ASC,
  ENUM_OPTION_SORT_MODE_LABEL_DESC,
]);

export const ENUM_OPTION_VIEW_SORT_MODES = Object.freeze([
  ENUM_OPTION_SORT_MODE_INHERIT,
  ...ENUM_OPTION_SORT_MODES,
]);

export const ENUM_OPTIONS_MODES = Object.freeze([
  ENUM_OPTIONS_MODE_FIXED,
  ENUM_OPTIONS_MODE_MANAGED,
]);

/**
 * Whether an options-list entry is a group header (not a selectable value).
 *
 * @param {unknown} option
 * @returns {boolean}
 */
export function isEnumOptionGroup(option) {
  return Boolean(option && typeof option === 'object' && option.kind === 'group');
}

/**
 * Selectable options only (excludes group headers).
 *
 * @param {unknown} options
 * @returns {object[]}
 */
export function getSelectableEnumOptions(options) {
  return normalizeEnumOptions(options).filter((option) => !isEnumOptionGroup(option));
}

/**
 * Normalize enum options into canonical objects with value + translations.
 * Accepts legacy string arrays and merges duplicate entries.
 * Supports group headers: `{ kind: 'group', id, translations|label }`.
 * Order is preserved.
 *
 * @param {object[]} options
 * @returns {Array<object>}
 */
export function normalizeEnumOptions(options) {
  if (!Array.isArray(options)) return [];

  const result = [];
  const seenValues = new Map();
  const seenGroupIds = new Set();

  options.forEach((opt, index) => {
    if (opt == null) return;

    if (typeof opt === 'object' && isEnumOptionGroup(opt)) {
      const id = sanitizeOptionId(opt.id) || createEnumOptionId();
      if (seenGroupIds.has(id)) return;
      seenGroupIds.add(id);

      const translations = normalizeOptionTranslations(opt.translations, opt.label, 'Group');
      const label = String(translations?.en?.label || '').trim() || 'Group';
      translations.en = { label };

      result.push({
        kind: 'group',
        id,
        value: `__group__${id}`,
        translations,
        sortOrder: Number.isFinite(Number(opt.sortOrder)) ? Number(opt.sortOrder) : index,
      });
      return;
    }

    if (typeof opt === 'string') {
      const rawValue = opt.trim();
      if (!rawValue) return;
      const nextOption = {
        id: buildLegacyEnumOptionId(rawValue),
        value: rawValue,
        translations: {
          en: { label: rawValue },
        },
        color: undefined,
        iconKey: undefined,
        note: undefined,
        system: false,
        hidden: false,
        disabled: false,
        sortOrder: index,
      };
      const existingIndex = seenValues.get(rawValue);
      if (existingIndex == null) {
        seenValues.set(rawValue, result.length);
        result.push(nextOption);
      } else {
        result[existingIndex] = mergeOption(result[existingIndex], nextOption);
      }
      return;
    }

    if (typeof opt === 'object') {
      const valueCandidate = opt.value ?? opt.label ?? '';
      const value = typeof valueCandidate === 'string'
        ? valueCandidate.trim()
        : String(valueCandidate);
      if (!value || value.startsWith('__group__')) return;

      const translations = normalizeOptionTranslations(opt.translations, opt.label, value);
      const color = typeof opt.color === 'string' && opt.color.trim().length
        ? opt.color.trim()
        : undefined;
      const iconKey = sanitizeOptionIconKey(opt.iconKey);
      const note = typeof opt.note === 'string' && opt.note.trim().length
        ? opt.note.trim()
        : undefined;
      const targetDuration = normalizeTargetDurationDays(opt.targetDuration);
      const id = sanitizeOptionId(opt.id) || buildLegacyEnumOptionId(value);
      const orderindex = Number.isFinite(Number(opt.orderindex))
        ? Number(opt.orderindex)
        : (Number.isFinite(Number(opt.orderIndex)) ? Number(opt.orderIndex) : undefined);
      const sortOrder = Number.isFinite(Number(opt.sortOrder))
        ? Number(opt.sortOrder)
        : (orderindex != null ? orderindex : index);
      const nextOption = {
        id,
        value,
        translations,
        color,
        iconKey,
        note,
        ...(targetDuration != null ? { targetDuration } : {}),
        system: opt.system === true,
        hidden: opt.hidden === true,
        disabled: opt.disabled === true,
        sortOrder,
        ...(orderindex != null ? { orderindex } : {}),
      };

      const existingIndex = seenValues.get(value);
      if (existingIndex == null) {
        seenValues.set(value, result.length);
        result.push(nextOption);
      } else {
        result[existingIndex] = mergeOption(result[existingIndex], nextOption);
      }
    }
  });

  return result;
}

/**
 * Generate a stable enum option value from a display label.
 * Example: "In Progress" → "in_progress"
 *
 * @param {string} label
 * @returns {string}
 */
export function generateEnumOptionValue(label) {
  const raw = typeof label === 'string' ? label : String(label || '');
  const snake = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  return snake || 'option';
}

/**
 * Normalize enum option values into a unique, trimmed string list.
 * This is intentionally string-only (no translations).
 *
 * @param {Array<string|object>} options
 * @returns {string[]}
 */
export function normalizeEnumOptionValues(options) {
  if (!Array.isArray(options)) return [];

  const seen = new Set();
  const out = [];

  options.forEach((opt) => {
    if (opt == null) return;
    if (isEnumOptionGroup(opt)) return;
    if (typeof opt === 'string') {
      const value = opt.trim();
      if (!value || value.startsWith('__group__') || seen.has(value)) return;
      seen.add(value);
      out.push(value);
      return;
    }
    if (typeof opt === 'object') {
      const candidate = opt.value ?? opt.label ?? '';
      const value = typeof candidate === 'string' ? candidate.trim() : String(candidate);
      if (!value || value.startsWith('__group__') || seen.has(value)) return;
      seen.add(value);
      out.push(value);
    }
  });

  return out;
}

export function normalizeEnumOptionsSortMode(sortMode) {
  const normalized = String(sortMode || '').trim().toLowerCase();
  if (ENUM_OPTION_SORT_MODES.includes(normalized)) return normalized;
  return ENUM_OPTION_SORT_MODE_MANUAL;
}

export function normalizeEnumOptionsMode(mode) {
  const normalized = String(mode || '').trim().toLowerCase();
  if (ENUM_OPTIONS_MODES.includes(normalized)) return normalized;
  return ENUM_OPTIONS_MODE_FIXED;
}

export function normalizeEnumOptionsViewSortMode(sortMode) {
  const normalized = String(sortMode || '').trim().toLowerCase();
  if (ENUM_OPTION_VIEW_SORT_MODES.includes(normalized)) return normalized;
  return ENUM_OPTION_SORT_MODE_INHERIT;
}

export function sortEnumOptions(options, { sortMode = ENUM_OPTION_SORT_MODE_MANUAL, language = 'en' } = {}) {
  const normalizedOptions = normalizeEnumOptions(options);
  const normalizedSortMode = normalizeEnumOptionsSortMode(sortMode);

  if (normalizedSortMode === ENUM_OPTION_SORT_MODE_MANUAL) {
    return normalizedOptions;
  }

  const direction = normalizedSortMode === ENUM_OPTION_SORT_MODE_LABEL_DESC ? -1 : 1;

  const sortOptionSegment = (segment) => [...segment].sort((left, right) => {
    const leftLabel = String(getOptionLabel(left, language) || left?.value || '');
    const rightLabel = String(getOptionLabel(right, language) || right?.value || '');

    const byLabel = leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });
    if (byLabel !== 0) return byLabel * direction;

    const leftValue = String(left?.value || '');
    const rightValue = String(right?.value || '');
    return leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' }) * direction;
  });

  // Keep group headers as section breaks; sort only within each section.
  const sorted = [];
  let segment = [];
  const flushSegment = () => {
    if (!segment.length) return;
    sorted.push(...sortOptionSegment(segment));
    segment = [];
  };

  normalizedOptions.forEach((option) => {
    if (isEnumOptionGroup(option)) {
      flushSegment();
      sorted.push(option);
      return;
    }
    segment.push(option);
  });
  flushSegment();

  return sorted;
}

export function resolveEnumOptionsForField(fieldDef, language = 'en', config = {}) {
  const options = fieldDef?.options || [];
  const fieldSortMode = normalizeEnumOptionsSortMode(fieldDef?.enumOptionsSort);
  const viewSortMode = normalizeEnumOptionsViewSortMode(config?.sortModeOverride);
  const effectiveSortMode = viewSortMode === ENUM_OPTION_SORT_MODE_INHERIT
    ? fieldSortMode
    : viewSortMode;

  const sorted = sortEnumOptions(options, { sortMode: effectiveSortMode, language });

  // Pickers use selectable options only; group headers are editor/display chrome.
  const withoutGroups = sorted.filter((option) => !isEnumOptionGroup(option));

  const hiddenOptionIds = Array.isArray(config?.hiddenOptionIds)
    ? [...new Set(config.hiddenOptionIds
      .map((value) => String(value || '').trim())
      .filter(Boolean))]
    : [];

  const hiddenSet = new Set(hiddenOptionIds);
  const visible = hiddenOptionIds.length
    ? withoutGroups.filter((option) => !hiddenSet.has(String(option?.id || '').trim()))
    : withoutGroups;

  const optionOrderIds = Array.isArray(config?.optionOrderIds)
    ? [...new Set(config.optionOrderIds
      .map((value) => String(value || '').trim())
      .filter(Boolean))]
    : [];

  if (!optionOrderIds.length) return visible;

  const usedIds = new Set();
  const optionById = new Map();
  visible.forEach((option) => {
    const optionId = String(option?.id || '').trim();
    if (!optionId) return;
    optionById.set(optionId, option);
  });

  const ordered = [];
  optionOrderIds.forEach((optionId) => {
    const option = optionById.get(optionId);
    if (!option) return;
    usedIds.add(optionId);
    ordered.push(option);
  });

  const remainder = visible.filter((option) => {
    const optionId = String(option?.id || '').trim();
    return !optionId || !usedIds.has(optionId);
  });

  return [...ordered, ...remainder];
}

export function buildEnumOptionValueRankMap(options) {
  const ranks = new Map();

  getSelectableEnumOptions(options).forEach((option, index) => {
    const value = String(option?.value || '').trim();
    if (!value || ranks.has(value)) return;
    ranks.set(value, index);
  });

  return ranks;
}

/**
 * Create a new unique enum option id for newly added options.
 */
export function createEnumOptionId() {
  const randomPart = Math.random().toString(36).slice(2, 10);
  const timePart = Date.now().toString(36);
  return `opt_${timePart}${randomPart}`;
}

/**
 * Create a new unique id for an options group header.
 */
export function createEnumGroupId() {
  const randomPart = Math.random().toString(36).slice(2, 10);
  const timePart = Date.now().toString(36);
  return `grp_${timePart}${randomPart}`;
}

function sanitizeOptionId(rawId) {
  if (rawId == null) return '';
  const candidate = String(rawId).trim();
  return candidate || '';
}

function sanitizeOptionIconKey(rawIconKey) {
  if (rawIconKey == null) return undefined;
  const candidate = String(rawIconKey).trim();
  return candidate || undefined;
}

function buildLegacyEnumOptionId(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return createEnumOptionId();
  return `legacy_${hashString(normalized)}`;
}

function hashString(input) {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Build a normalized translations map for an option.
 */
function normalizeOptionTranslations(translations, fallbackLabel, fallbackValue) {
  const out = {};

  if (translations && typeof translations === 'object') {
    Object.entries(translations).forEach(([lang, data]) => {
      if (!lang) return;
      let label;
      if (data && typeof data === 'object') {
        label = typeof data.label === 'string' ? data.label : undefined;
      } else if (typeof data === 'string') {
        label = data;
      }
      if (typeof label === 'string') {
        out[lang] = { label: label };
      }
    });
  }

  const fallback = fallbackLabel && typeof fallbackLabel === 'string' && fallbackLabel.trim().length
    ? fallbackLabel
    : fallbackValue;

  if (!out.en || typeof out.en.label !== 'string' || out.en.label.trim().length === 0) {
    const fallbackStr = fallback != null ? String(fallback) : '';
    out.en = { label: fallbackStr };
  }

  return out;
}

/**
 * Merge two option objects, preferring existing translations.
 */
function mergeOption(existing, next) {
  if (!existing) return next;
  if (!next) return existing;

  const translations = { ...existing.translations };
  Object.entries(next.translations || {}).forEach(([lang, data]) => {
    if (!data) return;
    if (!translations[lang] || !translations[lang].label) {
      translations[lang] = { label: data.label };
    }
  });

  const targetDuration = existing.targetDuration != null
    ? normalizeTargetDurationDays(existing.targetDuration)
    : normalizeTargetDurationDays(next.targetDuration);

  return {
    id: existing.id || next.id || createEnumOptionId(),
    value: existing.value,
    translations,
    color: existing.color || next.color,
    iconKey: existing.iconKey || next.iconKey,
    note: existing.note || next.note,
    ...(targetDuration != null ? { targetDuration } : {}),
    system: existing.system === true || next.system === true,
    hidden: existing.hidden === true || next.hidden === true,
    disabled: existing.disabled === true || next.disabled === true,
    ...(existing.sortOrder != null || next.sortOrder != null
      ? {
        sortOrder: existing.sortOrder != null ? existing.sortOrder : next.sortOrder,
      }
      : {}),
    ...(existing.orderindex != null || next.orderindex != null
      ? {
        orderindex: existing.orderindex != null ? existing.orderindex : next.orderindex,
      }
      : {}),
  };
}

/**
 * Return a list of option values in order.
 */
export function enumOptionValues(options) {
  return getSelectableEnumOptions(options).map((opt) => opt.value);
}

/**
 * Find an option object (string-safe) by value.
 * Exact match first; case-insensitive fallback so renames like confirmed→Confirmed
 * still resolve labels/colors for existing records.
 * Numeric values also match `orderindex` (ClickUp drop_down often stores index).
 */
export function findEnumOption(options, value) {
  if (!Array.isArray(options)) return null;
  if (value == null || value === '') return null;
  const target = String(value);

  let caseInsensitiveMatch = null;
  let orderIndexMatch = null;
  const asNumber = Number(value);
  const isNumericTarget = Number.isFinite(asNumber) && String(asNumber) === target.trim();

  for (const opt of options) {
    if (opt == null) continue;
    if (typeof opt === 'string') {
      if (opt === target) {
        return {
          id: buildLegacyEnumOptionId(opt),
          value: opt,
          translations: { en: { label: opt } },
          color: undefined,
          iconKey: undefined,
        };
      }
      if (!caseInsensitiveMatch && opt.toLowerCase() === target.toLowerCase()) {
        caseInsensitiveMatch = {
          id: buildLegacyEnumOptionId(opt),
          value: opt,
          translations: { en: { label: opt } },
          color: undefined,
          iconKey: undefined,
        };
      }
      continue;
    }
    if (typeof opt === 'object') {
      if (isEnumOptionGroup(opt)) continue;
      const optValue = opt.value != null ? String(opt.value) : undefined;
      if (optValue == null || optValue.startsWith('__group__')) continue;
      const translations = normalizeOptionTranslations(opt.translations, opt.label, optValue);
      const color = typeof opt.color === 'string' && opt.color.trim().length
        ? opt.color.trim()
        : undefined;
      const iconKey = sanitizeOptionIconKey(opt.iconKey);
      const resolved = {
        id: sanitizeOptionId(opt.id) || buildLegacyEnumOptionId(optValue),
        value: optValue,
        translations,
        color,
        iconKey,
      };
      if (optValue === target) return resolved;
      if (!caseInsensitiveMatch && optValue.toLowerCase() === target.toLowerCase()) {
        caseInsensitiveMatch = resolved;
      }
      if (
        isNumericTarget
        && !orderIndexMatch
        && (
          Number(opt.orderindex) === asNumber
          || Number(opt.orderIndex) === asNumber
        )
      ) {
        orderIndexMatch = resolved;
      }
    }
  }
  return caseInsensitiveMatch || orderIndexMatch;
}

/**
 * Resolve a label for an option value in the requested language.
 */
export function getEnumOptionLabel(options, value, language = 'en') {
  const option = findEnumOption(options, value);
  if (!option) return value != null ? String(value) : '';
  return getOptionLabel(option, language);
}

/**
 * Resolve a color for an option value.
 */
export function getEnumOptionColor(options, value) {
  const option = findEnumOption(options, value);
  return option?.color || '';
}

/**
 * Resolve an icon key for an option value.
 */
export function getEnumOptionIconKey(options, value) {
  const option = findEnumOption(options, value);
  return option?.iconKey || '';
}

/**
 * Resolve a label directly from an option object.
 */
export function getOptionLabel(option, language = 'en') {
  if (!option) return '';
  if (typeof option === 'string') return option;

  const translations = option.translations || {};
  const preferred = translations[language]?.label;
  if (preferred && preferred.trim().length) return preferred;

  const english = translations.en?.label;
  if (english && english.trim().length) return english;

  if (typeof option.label === 'string' && option.label.trim().length) return option.label;
  if (option.value != null) return String(option.value);
  return '';
}

/**
 * Update or insert a translation for a given option value.
 */
// Translation helpers removed for enum options (string-only).
